import { useState } from "react";

interface Props {
  symbols: string[];
  value: string;
  onSelect: (symbol: string) => void;
}

/**
 * Searchable single-ticker combobox. The input shows the current symbol; typing
 * filters a dropdown of matches (case-insensitive); choosing one calls onSelect.
 * Uses the shared `.app-search` input styling with a DeltaTape dropdown list.
 */
export function TickerPicker({ symbols, value, onSelect }: Props): JSX.Element {
  const [query, setQuery] = useState<string | null>(null); // null ⇒ showing the current value, list closed
  const open = query !== null;
  const q = (query ?? "").trim().toLowerCase();
  const matches = q ? symbols.filter((s) => s.toLowerCase().includes(q)) : symbols;

  function choose(symbol: string): void {
    onSelect(symbol);
    setQuery(null);
  }

  return (
    <span className="tape-picker">
      <span className="app-search">
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="6" cy="6" r="4.2" stroke="currentColor" strokeWidth="1.3" />
          <path d="M9.2 9.2 12 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={open ? (query ?? "") : value}
          onFocus={() => setQuery("")}
          onBlur={() => setQuery(null)}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Select symbol…"
          aria-label="Select symbol"
        />
      </span>
      {open ? (
        <ul className="tape-picker__list">
          {matches.length === 0 ? (
            <li className="tape-picker__empty">No matches</li>
          ) : (
            matches.map((s) => (
              <li
                key={s}
                className={`tape-picker__item${s === value ? " tape-picker__item--active" : ""}`}
                // mouseDown fires before the input's blur, so the choice registers.
                onMouseDown={() => choose(s)}
              >
                {s}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </span>
  );
}
